import { useState, type ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ChevronDown, ExternalLink } from 'lucide-react';
import { ButtonAnchor } from '@/components/ui/Button';
import { Banner } from '@/components/ui/Banner';
import { SectionCard } from '@/pages/settings/settingsChrome';
import { FORMS_LOGIN_URL } from '@/lib/formsUrls';
import { CLINIO_SUPPORT_EMAIL, clinioSupportMailto } from '@/lib/supportContact';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';

/** Status values already returned by `get_clinic_forms_status` on this page. */
export type FormsSetupStatus = 'not_provisioned' | 'pending' | 'active' | 'disabled';

const GUIDE_STEPS = [
  { titleKey: 'what_title', bodyKey: 'what_body' },
  { titleKey: 'activate_title', bodyKey: 'activate_body' },
  { titleKey: 'link_title', bodyKey: 'link_body' },
  { titleKey: 'admin_title', bodyKey: 'admin_body' },
] as const;

const ACTIVATE_EXTRA_KEYS = ['activate_account', 'activate_invite'] as const;

const TROUBLE_KEYS = [
  'trouble_retry',
  'trouble_admin',
  'trouble_forms_login',
  'trouble_email',
  'trouble_templates',
  'trouble_support',
] as const;

type TroubleKey = (typeof TROUBLE_KEYS)[number];

function troubleLinkKind(key: TroubleKey): 'forms' | 'support' | null {
  switch (key) {
    case 'trouble_forms_login':
      return 'forms';
    case 'trouble_support':
      return 'support';
    case 'trouble_retry':
    case 'trouble_admin':
    case 'trouble_email':
    case 'trouble_templates':
      return null;
    default: {
      const _exhaustive: never = key;
      return _exhaustive;
    }
  }
}

function statusNoteKey(status: FormsSetupStatus): string | null {
  switch (status) {
    case 'pending':
      return 'questionnaires.setup_guide.status_pending';
    case 'disabled':
      return 'questionnaires.setup_guide.status_disabled';
    case 'active':
    case 'not_provisioned':
      return null;
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

function statusNoteTone(status: FormsSetupStatus): 'info' | 'warning' | null {
  switch (status) {
    case 'pending':
      return 'info';
    case 'disabled':
      return 'warning';
    case 'active':
    case 'not_provisioned':
      return null;
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

function GuideLink({
  href,
  external = false,
  children,
}: {
  href: string;
  external?: boolean;
  children?: ReactNode;
}) {
  return (
    <a
      href={href}
      className="font-medium text-ds-teal underline underline-offset-2"
      {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
    >
      {children}
    </a>
  );
}

function SetupGuideRichText({
  i18nKey,
  as: Tag = 'p',
  className,
}: {
  i18nKey: string;
  as?: 'p' | 'span';
  className?: string;
}) {
  return (
    <Tag className={className}>
      <Trans
        i18nKey={i18nKey}
        values={{ supportEmail: CLINIO_SUPPORT_EMAIL }}
        components={{
          formsLogin: <GuideLink href={FORMS_LOGIN_URL} external />,
          supportMail: <GuideLink href={clinioSupportMailto()} />,
        }}
      />
    </Tag>
  );
}

export function FormsLinkingHelp({
  errorMessage,
  className,
}: {
  errorMessage?: string;
  className?: string;
}) {
  const { t } = useTranslation();

  return (
    <div className={cn('min-w-0', className)}>
      <p className={cn(TYPE.section, 'text-ds-text')}>
        {t('questionnaires.setup_guide.trouble_title')}
      </p>
      <p className={cn(TYPE.body, 'mt-s1 text-ds-muted')}>
        {t('questionnaires.setup_guide.trouble_intro')}
      </p>
      {errorMessage ? (
        <p className={cn(TYPE.small, 'mt-s2 font-medium text-ds-red-ink')}>
          {t('questionnaires.setup_guide.shown_error', { error: errorMessage })}
        </p>
      ) : null}
      <ol className="mt-s3 flex flex-col gap-s2">
        {TROUBLE_KEYS.map((key, index) => {
          const linkKind = troubleLinkKind(key);
          return (
            <li key={key} className="flex gap-s3">
              <span
                aria-hidden
                className={cn(
                  TYPE.meta,
                  'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-ds-neutral-tint text-ds-muted',
                )}
              >
                {index + 1}
              </span>
              {linkKind ? (
                <SetupGuideRichText
                  i18nKey={`questionnaires.setup_guide.${key}`}
                  className={cn(TYPE.body, 'text-ds-text')}
                />
              ) : (
                <span className={cn(TYPE.body, 'text-ds-text')}>
                  {t(`questionnaires.setup_guide.${key}`)}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function FormsSetupGuideBody({
  connected,
  openFormsHref,
  openFormsAction,
  status,
}: {
  connected: boolean;
  openFormsHref?: string;
  openFormsAction?: ReactNode;
  status: FormsSetupStatus;
}) {
  const { t } = useTranslation();
  const noteKey = statusNoteKey(status);
  const noteTone = statusNoteTone(status);

  return (
    <div className="flex min-w-0 flex-col gap-s4">
      {noteKey && noteTone ? (
        <Banner tone={noteTone}>
          <SetupGuideRichText i18nKey={noteKey} as="span" />
        </Banner>
      ) : null}

      <ol className="flex flex-col gap-s4">
        {GUIDE_STEPS.map((step, index) => (
          <li key={step.titleKey} className="flex gap-s3">
            <span
              aria-hidden
              className={cn(
                TYPE.meta,
                'mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-ds-teal-tint text-ds-teal',
              )}
            >
              {index + 1}
            </span>
            <div className="min-w-0 flex-1">
              <h4 className={cn(TYPE.section, 'text-ds-text')}>
                {t(`questionnaires.setup_guide.${step.titleKey}`)}
              </h4>
              <SetupGuideRichText
                i18nKey={`questionnaires.setup_guide.${step.bodyKey}`}
                className={cn(TYPE.body, 'mt-s1 text-ds-muted')}
              />
              {step.titleKey === 'activate_title'
                ? ACTIVATE_EXTRA_KEYS.map((key) => (
                    <SetupGuideRichText
                      key={key}
                      i18nKey={`questionnaires.setup_guide.${key}`}
                      className={cn(TYPE.body, 'mt-s2 text-ds-muted')}
                    />
                  ))
                : null}
              {step.titleKey === 'activate_title' ? (
                <ButtonAnchor
                  href={FORMS_LOGIN_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="link"
                  className="mt-s3"
                >
                  <ExternalLink />
                  {t('questionnaires.setup_guide.activate_login_link')}
                </ButtonAnchor>
              ) : null}
              {step.titleKey === 'admin_title' && connected ? (
                openFormsAction ? (
                  <div className="mt-s3">{openFormsAction}</div>
                ) : openFormsHref ? (
                  <ButtonAnchor
                    href={openFormsHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="secondary"
                    className="mt-s3"
                  >
                    <ExternalLink />
                    {t('questionnaires.setup_guide.admin_link')}
                  </ButtonAnchor>
                ) : null
              ) : null}
            </div>
          </li>
        ))}
        <li className="flex gap-s3">
          <span
            aria-hidden
            className={cn(
              TYPE.meta,
              'mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-ds-teal-tint text-ds-teal',
            )}
          >
            5
          </span>
          <FormsLinkingHelp className="min-w-0 flex-1" />
        </li>
      </ol>
    </div>
  );
}

export function FormsSetupGuide({
  connected,
  openFormsHref,
  openFormsAction,
  status,
  className,
}: {
  connected: boolean;
  openFormsHref?: string;
  openFormsAction?: ReactNode;
  status: FormsSetupStatus;
  className?: string;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  if (!connected) {
    return (
      <div className={className}>
        <SectionCard title={t('questionnaires.setup_guide.title_disconnected')}>
          <FormsSetupGuideBody
            connected={false}
            openFormsHref={openFormsHref}
            openFormsAction={openFormsAction}
            status={status}
          />
        </SectionCard>
      </div>
    );
  }

  return (
    <section className={cn('rounded-card border border-ds-line bg-ds-card shadow-card', className)}>
      <h3>
        <button
          type="button"
          id="forms-setup-guide-heading"
          aria-expanded={open}
          aria-controls="forms-setup-guide-panel"
          aria-label={
            open
              ? t('questionnaires.setup_guide.collapse')
              : t('questionnaires.setup_guide.expand')
          }
          className="flex w-full items-center justify-between gap-s3 p-s4 text-left focus:outline-none focus-visible:shadow-ring nav:p-s5"
          onClick={() => setOpen((value) => !value)}
        >
          <span className={cn(TYPE.section, 'text-ds-text')}>
            {t('questionnaires.setup_guide.title')}
          </span>
          <ChevronDown
            className={cn(
              'h-4 w-4 shrink-0 text-ds-muted transition-transform',
              open && 'rotate-180',
            )}
            aria-hidden
          />
        </button>
      </h3>
      <div
        id="forms-setup-guide-panel"
        role="region"
        aria-labelledby="forms-setup-guide-heading"
        hidden={!open}
        className="border-t border-ds-line px-s4 pb-s4 nav:px-s5 nav:pb-s5"
      >
        <FormsSetupGuideBody
          connected
          openFormsHref={openFormsHref}
          openFormsAction={openFormsAction}
          status={status}
        />
      </div>
    </section>
  );
}
